import { computed, signal } from '@preact/signals';
import { authApi, type UserMe } from '@/lib/api/endpoints';
import {
  clearLegacyAuthStorage,
  registerSessionExpiredHandler,
  resetSessionExpired,
} from '@/lib/api/client';
import { reset } from './queryCache';
import { currentRole } from './rbac';
import { notifications } from './notifications';
import { activeJobs } from './jobs';

export type AuthStatus = 'idle' | 'loading' | 'authed' | 'guest' | 'error';

export const currentUser = signal<UserMe | null>(null);
export const authStatus = signal<AuthStatus>('idle');
export const authError = signal<string | null>(null);

export const isAuthed = computed(() => authStatus.value === 'authed' && currentUser.value !== null);
export const isLoadingAuth = computed(
  () => authStatus.value === 'loading' || authStatus.value === 'idle',
);

function applyUser(user: UserMe | null) {
  const role = user?.global_role ?? user?.role ?? null;
  currentUser.value = user && role ? { ...user, global_role: role } : user;
  currentRole.value = role as typeof currentRole.value;
}

function resetCompanyScopedData() {
  // Hard-reset all company-scoped data on tenant switch: clears stored values
  // (so the UI shows loading instead of the previous tenant's data) and
  // discards any fetch already in flight under the previous scope.
  const prefixes = [
    'biblioteca',
    'canales',
    'configuracion',
    'economia',
    'governance',
    'leads',
    'library',
    'naming',
    'reporting',
    'config', // Integration configs (meta, shopify, drive, analytics)
    'sync-overview', // Sync status for integrations
    'integration',
    'business',
    'cdp',
    'custom',
    'clients',
    'users',
    'companies',
  ];
  prefixes.forEach(reset);
  // Clear stores that live outside the queryCache and are also tenant-scoped.
  notifications.value = [];
  activeJobs.value = [];
}

/** Actualiza el usuario en memoria tras un PATCH /users/me (u otra mutación
 * que devuelva el UserResponse fresco). No toca cookies ni authStatus. */
export function updateCurrentUser(user: UserMe): void {
  applyUser(user);
}

/** Run once at app start. Resolves regardless of auth result. */
export async function bootstrapAuth(): Promise<void> {
  if (authStatus.value === 'authed') return;
  authStatus.value = 'loading';
  authError.value = null;
  try {
    const { data, error } = await authApi.me();
    if (error || !data) {
      applyUser(null);
      authStatus.value = 'guest';
      return;
    }
    applyUser(data as unknown as UserMe);
    authStatus.value = 'authed';
  } catch {
    applyUser(null);
    authStatus.value = 'guest';
  }
}

registerSessionExpiredHandler(() => {
  applyUser(null);
  authStatus.value = 'guest';
});

export async function login(email: string, password: string): Promise<void> {
  authError.value = null;
  resetSessionExpired();
  const { data, error } = await authApi.login({ email, password });
  if (error) {
    const message =
      typeof error === 'object' && error !== null && 'detail' in error
        ? String((error as { detail?: string }).detail ?? 'Credenciales inválidas')
        : 'Credenciales inválidas';
    authError.value = message;
    throw new Error(message);
  }
  // Auth viaja solo en cookies httpOnly que el backend setea en esta respuesta.
  // Limpiamos cualquier residuo de localStorage de sesiones previas a la
  // migración cookie-only.
  clearLegacyAuthStorage();
  if (data?.user) {
    applyUser(data.user);
    authStatus.value = 'authed';
  } else {
    await bootstrapAuth();
  }
}

export async function logout(): Promise<void> {
  try {
    await authApi.logout();
  } catch {
    // ignore
  }
  // Cookies httpOnly se limpian server-side (Set-Cookie con max-age=0 en la
  // respuesta de /auth/logout). Aquí solo limpiamos cualquier residuo legacy
  // de localStorage por si el usuario venía de una sesión anterior a la
  // migración cookie-only.
  clearLegacyAuthStorage();
  applyUser(null);
  authStatus.value = 'guest';
}

export async function reloadUser(): Promise<void> {
  const { data } = await authApi.me();
  if (data) applyUser(data as unknown as UserMe);
}

export async function switchCompany(companyId: number): Promise<void> {
  authError.value = null;
  const { data, error } = await authApi.switchCompany({ company_id: companyId });
  if (error || !data) {
    const message =
      typeof error === 'object' && error !== null && 'detail' in error
        ? String((error as { detail?: string }).detail ?? 'No se pudo cambiar de empresa')
        : 'No se pudo cambiar de empresa';
    authError.value = message;
    throw new Error(message);
  }
  applyUser(data as unknown as UserMe);
  resetCompanyScopedData();
  // Also clear company-scoped localStorage (sync profile, UI state, etc.)
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('configuracion.') || key.startsWith('onmi.'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  } catch {
    // Ignore localStorage errors (private mode, quota, etc.)
  }
}
